import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { WaziWordmark, WaziMark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, Download, AlertCircle, Star } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const QuoteApproval = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState(null); // 'approved' | 'rejected' | null
  const [showDecline, setShowDecline] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/quotes/${quoteId}/view?t=${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [quoteId, token]);

  const send = async (verb) => {
    setSubmitting(true);
    try {
      await api.post(`/public/quotes/${quoteId}/${verb}`, { token, message });
      setDecision(verb === "approve" ? "approved" : "rejected");
      trackEvent(`quote_${verb}d`, { quote_id: quoteId });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) toast.error("This link is invalid or expired.");
      else if (status === 409) toast.error("This quote was already actioned.");
      else toast.error("Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#718096]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  if (data.error) return (
    <div className="min-h-screen bg-[#F7FAFC] flex flex-col items-center justify-center p-6">
      <AlertCircle className="w-12 h-12 text-[#DC2626]" />
      <h2 className="font-heading mt-4 text-2xl font-bold">Quote not found</h2>
      <Link to="/"><Button className="mt-6 rounded-full">Back home</Button></Link>
    </div>
  );

  const { quote, worker, customer } = data;
  const status = decision || quote.status;
  const pdfUrl = `${API}/public/quotes/${quote.id}/pdf`;

  if (status === "approved" || status === "rejected") {
    const approved = status === "approved";
    return (
      <div className="min-h-screen bg-[#F7FAFC] flex flex-col items-center justify-center p-4">
        <Link to="/" className="mb-8"><WaziWordmark size={36} /></Link>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 max-w-lg w-full text-center shadow-sm" data-testid="quote-decision-card">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${approved ? "bg-[#25D366]/10" : "bg-[#F7FAFC]"}`}>
            {approved ? <CheckCircle2 className="w-9 h-9 text-[#25D366]" /> : <XCircle className="w-9 h-9 text-[#DC2626]" />}
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#2D3748] mt-4">
            {approved ? "Quote approved! 🎉" : "Quote declined"}
          </h2>
          {approved ? (
            <p className="mt-2 text-[#718096]">We've let <b>{worker?.business_name}</b> know. They'll be in touch on WhatsApp to confirm scheduling. Pay the deposit using the bank details in your quote to lock in the booking.</p>
          ) : (
            <p className="mt-2 text-[#718096]">We've let {worker?.business_name} know. No problem — when you're ready for another quote, just message Wazi.</p>
          )}
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
            <Button className="mt-6 rounded-full bg-[#1E88E5] hover:bg-[#1565C0] text-white" data-testid="post-decision-pdf">
              <Download className="w-4 h-4 mr-2" /> Download quote PDF
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FAFC] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6"><Link to="/"><WaziWordmark size={32} /></Link></div>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden shadow-sm" data-testid="quote-approval-card">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#25D366] to-[#16A34A] text-white p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-white/80 text-xs uppercase tracking-widest font-heading font-semibold">Quote {quote.quote_number}</div>
                <h1 className="font-heading text-2xl font-bold mt-1">Hi {(customer?.name || "").split(" ")[0] || "there"} 👋</h1>
              </div>
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" data-testid="quote-pdf-btn" className="rounded-full border-white/40 text-white hover:bg-white hover:text-[#16A34A] bg-white/10">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                </Button>
              </a>
            </div>
            <p className="mt-3 text-white/90 text-sm">{worker?.business_name} sent you a quote for the work below. Tap a button to respond.</p>
          </div>

          {/* Worker card */}
          <div className="px-6 pt-5 pb-4 flex items-center gap-4 border-b border-[#E2E8F0]">
            {worker?.avatar_url ? (
              <img src={worker.avatar_url} alt="" className="w-14 h-14 rounded-2xl object-cover border border-[#E2E8F0]" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-[#25D366]/10 flex items-center justify-center"><WaziMark size={28} /></div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold text-[#2D3748]">{worker?.business_name}</div>
              <div className="text-sm text-[#718096] flex items-center gap-2">
                <Star className="w-3.5 h-3.5 text-[#FFB400] fill-[#FFB400]" />
                {(worker?.rating_avg || 0).toFixed(1)} · {worker?.reviews_count || 0} reviews
                <Link to={`/u/${worker?.slug}`} target="_blank" className="text-[#1E88E5] hover:underline ml-1">View profile →</Link>
              </div>
            </div>
          </div>

          {/* Quote body */}
          <div className="px-6 py-5">
            <div className="text-xs uppercase tracking-widest text-[#718096] font-heading font-semibold">Work</div>
            <p className="mt-1 text-[#2D3748]">{quote.work_description}</p>

            <table className="w-full text-sm mt-6">
              <thead>
                <tr className="text-xs text-[#718096] uppercase tracking-wider">
                  <th className="text-left py-2">Item</th>
                  <th className="text-right py-2">Qty</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {(quote.line_items || []).map((li, i) => (
                  <tr key={i}>
                    <td className="py-2.5 text-[#2D3748]">{li.description}</td>
                    <td className="py-2.5 text-right text-[#718096]">{li.quantity}</td>
                    <td className="py-2.5 text-right text-[#2D3748] font-medium">R {li.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-5 pt-4 border-t border-[#E2E8F0] grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[#718096] text-xs">Deposit (to confirm)</div>
                <div className="font-heading font-bold text-[#16A34A] text-lg">R {(quote.deposit || 0).toFixed(2)}</div>
              </div>
              <div className="text-right">
                <div className="text-[#718096] text-xs">Total</div>
                <div className="font-heading font-bold text-[#2D3748] text-2xl">R {(quote.total || 0).toFixed(2)}</div>
              </div>
            </div>

            {quote.payment_instructions && (
              <div className="mt-5 p-3 bg-[#F7FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#2D3748] whitespace-pre-line">
                <div className="font-heading font-semibold mb-1">Payment instructions</div>
                {quote.payment_instructions}
              </div>
            )}

            {quote.notes && <div className="mt-3 text-sm italic text-[#718096]">"{quote.notes}"</div>}
          </div>

          {/* CTAs */}
          {data.valid_token ? (
            <div className="px-6 pb-6 pt-2 space-y-3" data-testid="quote-ctas">
              {showDecline ? (
                <div className="space-y-3">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    placeholder="(Optional) Let the painter know why — too pricey, timing not right, etc."
                    data-testid="decline-reason"
                    className="w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 outline-none focus:border-[#DC2626] text-sm"
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => setShowDecline(false)} variant="outline" className="rounded-full" data-testid="decline-cancel">Cancel</Button>
                    <Button onClick={() => send("decline")} disabled={submitting} data-testid="decline-confirm" className="flex-1 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm decline"}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button
                    onClick={() => send("approve")}
                    disabled={submitting}
                    data-testid="approve-quote-btn"
                    className="w-full rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white py-6 font-medium wazi-cta-glow text-base"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> Approve quote — let's go</>}
                  </Button>
                  <Button
                    onClick={() => setShowDecline(true)}
                    variant="outline"
                    data-testid="decline-quote-btn"
                    className="w-full rounded-full border-[#E2E8F0] text-[#718096] hover:bg-[#F7FAFC]"
                  >
                    Decline politely
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="px-6 pb-6 text-center text-xs text-[#DC2626]" data-testid="invalid-token">
              This link is missing a valid security token. Please open it from the WhatsApp message we sent you.
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[#718096] mt-6">Powered by Wazi · Your business. Simplified.</p>
      </div>
    </div>
  );
};

export default QuoteApproval;
